import { MagazineContextProvider } from '@/contexts/Magazine/Magazine.context';
import Clickable from '@uikit/components/Clickable/Clickable';
import { ClickableProps } from '@uikit/components/Clickable/interfaces';
import Icon from '@uikit/components/Icon/Icon';
import LegacyPicture from '@uikit/components/LegacyPicture/LegacyPicture';
import clsx from 'clsx';
import React from 'react';
import styles from './MagazineAmazingNumber.module.scss';
import { breakpointAspectRatioConfigs } from './consts';
import type { MagazineAmazingNumberProps } from './interfaces';

const MagazineProviderWrapper = ({
  children,
  category,
}: {
  category?: string;
  children: React.ReactNode;
}) =>
  category ? (
    <MagazineContextProvider value={{ currentCategory: category }}>
      {children}
    </MagazineContextProvider>
  ) : (
    <>{children}</>
  );

const MagazineAmazingNumberWrapper = ({
  className,
  displayLink,
  children,
  clickableProps,
}: {
  className: string;
  displayLink?: boolean;
  clickableProps: ClickableProps;
  children: React.ReactNode;
}) => {
  if (displayLink) {
    return (
      <Clickable className={className} {...clickableProps}>
        {children}
      </Clickable>
    );
  }

  return <div className={className}>{children}</div>;
};

const MagazineAmazingNumber = ({
  number,
  title,
  text,
  type = 'simple',
  transformedImage,
  category,
  displayLink,
  clickableProps,
}: MagazineAmazingNumberProps) => {
  const hasImage = type === 'highlighted' && !!transformedImage?.path;

  return (
    <MagazineProviderWrapper category={category}>
      <MagazineAmazingNumberWrapper
        className={clsx(styles.base)}
        displayLink={displayLink}
        clickableProps={clickableProps}
      >
        <div className={clsx(styles.baseWrapper, styles[type])}>
          <div className={styles.wrapper}>
            <div
              className={clsx(styles.contentWrapper, {
                [styles.contentWithImage]: hasImage,
              })}
            >
              <div
                className={clsx(styles.number, {
                  [styles.numberMedium]: number.length >= 5,
                  [styles.numberLarge]: number.length >= 8,
                })}
              >
                {number}
              </div>
              <div className={styles.title}>{title}</div>
              <div className={styles.text}>{text}</div>
              {displayLink && (
                <div>
                  <Icon variant={'action/arrow-right'} size="iconSize24" />
                </div>
              )}
            </div>
            {hasImage && (
              <div className={styles.imageBottom}>
                <LegacyPicture
                  asset={transformedImage}
                  breakpointAspectRatioConfigs={breakpointAspectRatioConfigs}
                  defaultWidth={320}
                />
              </div>
            )}
          </div>
        </div>
      </MagazineAmazingNumberWrapper>
    </MagazineProviderWrapper>
  );
};

export default MagazineAmazingNumber;
